import { useContext, useEffect, useRef, useState } from "react"
import { FiChevronDown, FiChevronUp, FiPlus, FiZap } from "react-icons/fi"
import { api } from "../../../api/api"
import { MenuContext } from "../../../context/MenuContext"
import { ProductContext } from "../../../context/ProductContext"
import { Toast } from "../../../utils/Toast"
import { mascaraMoeda, maskCurrency } from "../../../utils/utils"
import "../../../pages/admin.css"
import "./style.css"

const emptyAdditional = () => ({ name: "", pricing: 0, active: false })

function normalizePrice(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0
  if (typeof value !== "string" || !value.trim()) return 0

  const directValue = Number(value)
  if (Number.isFinite(directValue)) return directValue

  const localizedValue = Number(value.replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", "."))
  return Number.isFinite(localizedValue) ? localizedValue : 0
}

const emptyGroup = () => ({
  name: "",
  min: 0,
  max: 1,
  active: false,
  useHighestItemPrice: false,
  additionals: [emptyAdditional()]
})

export function ProductAiModal({ isMenu = true, isLocal = false, loadProducts }) {
  const { categoryId, load } = useContext(MenuContext)
  const { findAllSectors, listSector } = useContext(ProductContext)
  const [categories, setCategories] = useState([])
  const [step, setStep] = useState("prompt")
  const [prompt, setPrompt] = useState("")
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId || "")
  const [channels, setChannels] = useState({ isMenu, isLocal })
  const [draft, setDraft] = useState(null)
  const [openGroup, setOpenGroup] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const requestVersion = useRef(0)

  const compatibleCategories = categories.filter(category => {
    const supportsMenu = !channels.isMenu || Boolean(category.isMenu)
    const supportsLocal = !channels.isLocal || Boolean(category.isLocal)
    return Boolean(category.active) && supportsMenu && supportsLocal
  })

  useEffect(() => {
    const modal = document.getElementById("modalProductAi")
    const refreshReferenceData = async () => {
      try {
        const [response] = await Promise.all([api.get("/category"), findAllSectors()])
        setCategories(response.data)
      } catch (error) {}
    }
    refreshReferenceData()
    modal?.addEventListener("show.bs.modal", refreshReferenceData)
    return () => modal?.removeEventListener("show.bs.modal", refreshReferenceData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setSelectedCategoryId(categoryId || "")
  }, [categoryId])

  useEffect(() => {
    setChannels({ isMenu, isLocal })
  }, [isMenu, isLocal])

  function reset() {
    requestVersion.current += 1
    setStep("prompt")
    setPrompt("")
    setSelectedCategoryId(categoryId || "")
    setChannels({ isMenu, isLocal })
    setDraft(null)
    setOpenGroup(0)
    setLoading(false)
    setSaving(false)
  }

  function toggleChannel(channel) {
    const nextChannels = { ...channels, [channel]: !channels[channel] }
    setChannels(nextChannels)
    const category = categories.find(item => item.id === selectedCategoryId)
    if (category) {
      const supportsMenu = !nextChannels.isMenu || Boolean(category.isMenu)
      const supportsLocal = !nextChannels.isLocal || Boolean(category.isLocal)
      if (!supportsMenu || !supportsLocal) setSelectedCategoryId("")
    }
  }

  async function generateDraft() {
    if (prompt.trim().length < 3) return Toast.warning("Descreva o produto que deseja cadastrar.")
    if (!channels.isMenu && !channels.isLocal) return Toast.warning("Selecione pelo menos um canal de venda.")

    const currentRequest = requestVersion.current + 1
    requestVersion.current = currentRequest
    try {
      setLoading(true)
      const response = await api.post("/product-assistant/draft", {
        prompt,
        isMenu: channels.isMenu,
        isLocal: channels.isLocal,
        categoryId: selectedCategoryId || null
      })
      if (requestVersion.current !== currentRequest) return
      const data = response.data
      setDraft({
        product: {
          ...data.product,
          pricing: normalizePrice(data.product.pricing),
          active: false
        },
        groups: data.groups.map(group => ({
          ...group,
          active: false,
          additionals: group.additionals.map(additional => ({
            ...additional,
            pricing: normalizePrice(additional.pricing),
            active: false
          }))
        }))
      })
      setOpenGroup(0)
      setStep("review")
    } catch (error) {
      if (requestVersion.current === currentRequest) {
        Toast.warning(error?.response?.data?.message || "Não foi possível gerar o cadastro agora.", 2500)
      }
    } finally {
      if (requestVersion.current === currentRequest) setLoading(false)
    }
  }

  function updateProduct(field, value) {
    setDraft(current => ({ ...current, product: { ...current.product, [field]: value } }))
  }

  function updateGroup(groupIndex, field, value) {
    setDraft(current => ({
      ...current,
      groups: current.groups.map((group, index) => index === groupIndex ? { ...group, [field]: value } : group)
    }))
  }

  function updateAdditional(groupIndex, additionalIndex, field, value) {
    setDraft(current => ({
      ...current,
      groups: current.groups.map((group, index) => index === groupIndex ? {
        ...group,
        additionals: group.additionals.map((additional, itemIndex) => itemIndex === additionalIndex
          ? { ...additional, [field]: value }
          : additional)
      } : group)
    }))
  }

  function addGroup() {
    if (draft.groups.length >= 5) return Toast.warning("O limite é de cinco grupos.")
    const nextGroupIndex = draft.groups.length
    setDraft(current => ({ ...current, groups: [...current.groups, emptyGroup()] }))
    setOpenGroup(nextGroupIndex)
  }

  function removeGroup(groupIndex) {
    setDraft(current => ({ ...current, groups: current.groups.filter((_, index) => index !== groupIndex) }))
    setOpenGroup(current => current === groupIndex ? -1 : Math.min(current, draft.groups.length - 2))
  }

  function addAdditional(groupIndex) {
    if (draft.groups[groupIndex].additionals.length >= 10) return Toast.warning("O limite é de dez opções por grupo.")
    setDraft(current => ({
      ...current,
      groups: current.groups.map((group, index) => index === groupIndex
        ? { ...group, additionals: [...group.additionals, emptyAdditional()] }
        : group)
    }))
  }

  function removeAdditional(groupIndex, additionalIndex) {
    if (draft.groups[groupIndex].additionals.length === 1) {
      return Toast.warning("Cada grupo precisa ter pelo menos uma opção.")
    }
    setDraft(current => ({
      ...current,
      groups: current.groups.map((group, index) => index === groupIndex
        ? { ...group, additionals: group.additionals.filter((_, itemIndex) => itemIndex !== additionalIndex) }
        : group)
    }))
  }

  function validPrice(value) {
    const number = Number(value)
    const hasAtMostTwoDecimals = Math.abs(number * 100 - Math.round(number * 100)) < 0.000001
    return value !== "" && value !== null && Number.isFinite(number) && number >= 0 && number <= 99999999.99 && hasAtMostTwoDecimals
  }

  function validateDraft() {
    if (!draft.product.name.trim()) return "Confira o nome do produto."
    if (!draft.product.categoryId) return "Selecione a categoria do produto."
    if (!validPrice(draft.product.pricing)) return "Informe um preço válido para o produto."

    const invalidGroup = draft.groups.some(group => {
      const min = Number(group.min)
      const max = Number(group.max)
      const invalidLimits = group.min === "" || group.max === "" || !Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < min || max > 20
      const invalidOptions = group.additionals.some(additional => !additional.name.trim() || !validPrice(additional.pricing))
      return !group.name.trim() || invalidLimits || invalidOptions
    })
    return invalidGroup ? "Revise nomes, limites e preços dos adicionais." : null
  }

  async function createProduct() {
    const validationMessage = validateDraft()
    if (validationMessage) return Toast.warning(validationMessage)

    try {
      setSaving(true)
      await api.post("/product-assistant/create", {
        product: {
          name: draft.product.name.trim(),
          codReferencia: null,
          pricing: Number(draft.product.pricing),
          oldPricing: 0,
          promotionalPrice: 0,
          description: draft.product.description.trim(),
          image: "",
          categoryId: draft.product.categoryId,
          isMenu: channels.isMenu,
          isLocal: channels.isLocal,
          active: Boolean(draft.product.active),
          ncm: "",
          cest: "",
          refFiscal: "",
          idSector: draft.product.idSector || null
        },
        groups: draft.groups.map(group => ({
          name: group.name.trim(),
          min: Number(group.min),
          max: Number(group.max),
          active: Boolean(group.active),
          useHighestItemPrice: Boolean(group.useHighestItemPrice),
          additionals: group.additionals.map(additional => ({
            name: additional.name.trim(),
            pricing: Number(additional.pricing),
            active: Boolean(additional.active)
          }))
        }))
      })
    } catch (error) {
      Toast.warning(error?.response?.data?.message || "Não foi possível criar o produto.", 2500)
      setSaving(false)
      return
    }

    Toast.success("Produto e adicionais criados.", 1200)
    document.getElementById("btn-close-modal-product-ai")?.click()
    try {
      loadProducts ? await loadProducts() : await load()
    } catch (error) {
      Toast.info("Produto criado. Atualize a página para recarregar a listagem.", 2500)
    }
  }

  const missingPrices = draft
    ? Number(!validPrice(draft.product.pricing)) + draft.groups.reduce((total, group) => total + group.additionals.filter(additional => !validPrice(additional.pricing)).length, 0)
    : 0

  return (
    <div className="modal fade admin-form-modal product-ai-modal" id="modalProductAi" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="productAiModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
        <div className="modal-content">
          <div className="modal-header product-ai-modal-header">
            <div className="product-ai-title">
              <span><FiZap /></span>
              <div><h5 className="modal-title" id="productAiModalLabel">Criar produto com IA</h5><p>Gere um rascunho e revise tudo antes de salvar</p></div>
            </div>
            <button type="button" id="btn-close-modal-product-ai" className="btn-close" data-bs-dismiss="modal" aria-label="Fechar" onClick={reset}></button>
          </div>

          <div className="product-ai-steps" aria-label="Etapas do cadastro">
            <span className={step === "prompt" ? "active" : "done"}><b>1</b> Descrever</span>
            <i />
            <span className={step === "review" ? "active" : ""}><b>2</b> Revisar e criar</span>
          </div>

          <div className="modal-body product-ai-modal-body">
            {step === "prompt" ? (
              <div className="product-ai-start">
                <div className="product-ai-start-copy"><span>DeepSeek V4 Pro</span><h3>Qual produto você quer cadastrar?</h3><p>Informe ingredientes, tamanhos e preços conhecidos. O assistente não inventará valores ausentes.</p></div>
                <label className="product-ai-prompt-field">Descrição do produto
                  <textarea maxLength={600} value={prompt} onChange={event => setPrompt(event.target.value)} placeholder="Ex.: X-Bacon com hambúrguer, queijo, bacon e molho especial. Produto por R$ 28,90." autoFocus />
                  <small>{prompt.length}/600 caracteres</small>
                </label>
                <div className="product-ai-start-grid">
                  <div><strong>Canais de venda</strong><div className="product-ai-channel-options">
                    <label className={channels.isMenu ? "selected" : ""}><input type="checkbox" checked={channels.isMenu} onChange={() => toggleChannel("isMenu")} /> Delivery</label>
                    <label className={channels.isLocal ? "selected" : ""}><input type="checkbox" checked={channels.isLocal} onChange={() => toggleChannel("isLocal")} /> Venda local</label>
                  </div></div>
                  <label>Categoria inicial
                    <select value={selectedCategoryId} onChange={event => setSelectedCategoryId(event.target.value)}>
                      <option value="">Deixar a IA escolher</option>
                      {compatibleCategories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
                    </select>
                  </label>
                </div>
                <div className="product-ai-start-tip"><strong>Exemplo completo</strong><span>“Pizza grande de calabresa por R$ 49,90, com bordas de cheddar e catupiry por R$ 8,00.”</span></div>
              </div>
            ) : (
              <div className="product-ai-review-layout">
                <aside className="product-ai-product-panel">
                  <div className="product-ai-section-title"><span>Produto</span><small>Dados principais</small></div>
                  <label>Nome<input maxLength={70} value={draft.product.name} onChange={event => updateProduct("name", event.target.value)} /></label>
                  <label>Preço<input type="text" inputMode="decimal" value={maskCurrency(draft.product.pricing)} onChange={event => updateProduct("pricing", mascaraMoeda(event.target.value))} placeholder="R$ 0,00" /></label>
                  <label>Categoria<select value={draft.product.categoryId || ""} onChange={event => updateProduct("categoryId", event.target.value || null)}><option value="">Selecione</option>{compatibleCategories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
                  <label>Setor<select value={draft.product.idSector || ""} onChange={event => updateProduct("idSector", event.target.value || null)}><option value="">Selecione</option>{listSector.map(sector => <option key={sector.id} value={sector.id}>{sector.name}</option>)}</select></label>
                  <label>Descrição<textarea maxLength={1000} value={draft.product.description} onChange={event => updateProduct("description", event.target.value)} /></label>
                  <label className="product-ai-switch"><input type="checkbox" checked={draft.product.active} onChange={event => updateProduct("active", event.target.checked)} /><span /> Produto ativo</label>
                  <div className="product-ai-channel-summary"><strong>Canais</strong>{channels.isMenu && <span>Delivery</span>}{channels.isLocal && <span>Venda local</span>}</div>
                </aside>

                <section className="product-ai-groups-panel">
                  <div className="product-ai-groups-heading"><div><span>Grupos de adicionais</span><small>{draft.groups.length} de 5 grupos</small></div><button type="button" className="admin-secondary-button" onClick={addGroup}><FiPlus /> Novo grupo</button></div>
                  {missingPrices > 0 && <div className="product-ai-price-warning"><strong>{missingPrices} {missingPrices === 1 ? "preço precisa" : "preços precisam"} ser informado</strong><span>Use R$ 0,00 apenas para opções gratuitas.</span></div>}
                  <div className="product-ai-accordion">
                    {draft.groups.length === 0 && <div className="product-ai-empty-groups"><strong>Nenhum grupo sugerido</strong><span>Você pode criar o produto sem adicionais ou adicionar um grupo manualmente.</span></div>}
                    {draft.groups.map((group, groupIndex) => {
                      const expanded = openGroup === groupIndex
                      return <article className={`product-ai-group-card ${expanded ? "expanded" : ""}`} key={groupIndex}>
                        <div className="product-ai-group-summary">
                          <button type="button" className="product-ai-expand" onClick={() => setOpenGroup(expanded ? -1 : groupIndex)} aria-expanded={expanded}>
                            {expanded ? <FiChevronUp /> : <FiChevronDown />}
                            <span><strong>{group.name || "Grupo sem nome"}</strong><small>{group.additionals.length} {group.additionals.length === 1 ? "opção" : "opções"} · mín. {group.min || 0} · máx. {group.max || 0}</small></span>
                          </button>
                          <label className="product-ai-switch compact"><input type="checkbox" checked={group.active} onChange={event => updateGroup(groupIndex, "active", event.target.checked)} /><span /> Ativo</label>
                          <button type="button" className="product-ai-remove" onClick={() => removeGroup(groupIndex)}>Remover</button>
                        </div>
                        {expanded && <div className="product-ai-group-editor">
                          <div className="product-ai-group-fields">
                            <label>Nome do grupo<input maxLength={30} value={group.name} onChange={event => updateGroup(groupIndex, "name", event.target.value)} /></label>
                            <label>Mínimo<input type="number" min="0" max="20" value={group.min} onChange={event => updateGroup(groupIndex, "min", event.target.value)} /></label>
                            <label>Máximo<input type="number" min="0" max="20" value={group.max} onChange={event => updateGroup(groupIndex, "max", event.target.value)} /></label>
                            <label className="product-ai-check"><input type="checkbox" checked={group.useHighestItemPrice} onChange={event => updateGroup(groupIndex, "useHighestItemPrice", event.target.checked)} /> Usar o maior preço</label>
                          </div>
                          <div className="product-ai-options-list">
                            <div className="product-ai-options-label"><strong>Opções</strong><span>Nome, preço e disponibilidade</span></div>
                            {group.additionals.map((additional, additionalIndex) => <div className="product-ai-option-row" key={additionalIndex}>
                              <input maxLength={30} value={additional.name} onChange={event => updateAdditional(groupIndex, additionalIndex, "name", event.target.value)} placeholder="Nome da opção" aria-label="Nome da opção" />
                              <input type="text" inputMode="decimal" value={maskCurrency(additional.pricing)} onChange={event => updateAdditional(groupIndex, additionalIndex, "pricing", mascaraMoeda(event.target.value))} placeholder="R$ 0,00" aria-label="Preço da opção" />
                              <label className="product-ai-switch compact"><input type="checkbox" checked={additional.active} onChange={event => updateAdditional(groupIndex, additionalIndex, "active", event.target.checked)} /><span /> Ativo</label>
                              <button type="button" className="product-ai-remove" onClick={() => removeAdditional(groupIndex, additionalIndex)}>Remover</button>
                            </div>)}
                            <button type="button" className="product-ai-add-option" onClick={() => addAdditional(groupIndex)}><FiPlus /> Adicionar opção</button>
                          </div>
                        </div>}
                      </article>
                    })}
                  </div>
                </section>
              </div>
            )}
          </div>

          <div className="modal-footer product-ai-modal-footer">
            {step === "prompt" ? <>
              <button type="button" className="admin-secondary-button" data-bs-dismiss="modal" onClick={reset}>Cancelar</button>
              <button type="button" className="admin-primary-button" onClick={generateDraft} disabled={loading}><FiZap /> {loading ? "Gerando cadastro..." : "Gerar cadastro"}</button>
            </> : <>
              <button type="button" className="admin-secondary-button" onClick={() => setStep("prompt")}>Voltar e ajustar descrição</button>
              <button type="button" className="admin-primary-button" onClick={createProduct} disabled={saving}>{saving ? "Criando produto..." : "Criar produto e adicionais"}</button>
            </>}
          </div>
        </div>
      </div>
    </div>
  )
}
